import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import GlassCard from '../components/shared/GlassCard';
import Button from '../components/shared/Button';
import RadarChart from '../components/shared/RadarChart';
import ActivityHeatmap from '../components/shared/ActivityHeatmap';
import {
  getAthleteById,
  getLogsForAthlete,
  getWorkoutsForAthlete,
  MOCK_RADAR_SCORES,
} from '../utils/mockData';
import { formatWorkOutput } from '../utils/engineStub';
import './AthleteProfilePage.css';

function formatLastActive(isoDate) {
  if (!isoDate) return 'Active now';
  const diffMs = Date.now() - new Date(isoDate).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function AthleteProfilePage() {
  const { athleteId } = useParams();
  const navigate = useNavigate();

  const athlete = getAthleteById(athleteId);

  if (!athlete) {
    return (
      <div className="athlete-profile-page">
        <GlassCard size="md" className="athlete-profile-page__empty">
          <h1 className="athlete-profile-page__title">Athlete not found</h1>
          <p className="athlete-profile-page__subtitle">The selected athlete profile could not be loaded.</p>
          <Button variant="ghost" size="md" onClick={() => navigate('/app/dashboard')}>
            Back to Dashboard
          </Button>
        </GlassCard>
      </div>
    );
  }

  const initials = athlete.name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const logs = getLogsForAthlete(athlete.id).slice().sort((a, b) => (a.date < b.date ? 1 : -1));
  const workouts = getWorkoutsForAthlete(athlete.id).slice().sort((a, b) => (a.scheduledDate < b.scheduledDate ? 1 : -1));

  const totalWU = logs.reduce((sum, log) => sum + log.workOutputAchieved, 0);
  const activityEntries = [
    ...logs.map((log) => ({ date: log.date, value: 2 })),
    ...workouts.map((workout) => ({ date: workout.scheduledDate, value: 1 })),
  ];

  const scores = MOCK_RADAR_SCORES[athlete.id] || MOCK_RADAR_SCORES.team;

  return (
    <div className="athlete-profile-page">
      <div className="athlete-profile-page__header">
        <Link to="/app/dashboard" className="athlete-profile-page__back-link">
          Back to Dashboard
        </Link>
      </div>

      <GlassCard size="md" className="athlete-profile-page__identity">
        <div className="athlete-profile-page__avatar">{initials}</div>
        <div className="athlete-profile-page__identity-info">
          <h1 className="athlete-profile-page__name">{athlete.name}</h1>
          <p className="athlete-profile-page__meta">{athlete.position}</p>
          <p className="athlete-profile-page__meta">Last active {formatLastActive(athlete.lastActive)}</p>
          <div className="athlete-profile-page__chips">
            <span className="athlete-profile-page__chip">{athlete.streak}d streak</span>
            <span className="athlete-profile-page__chip">{logs.length} sessions logged</span>
            <span className="athlete-profile-page__chip">{formatWorkOutput(totalWU)} total output</span>
          </div>
        </div>
      </GlassCard>

      <GlassCard size="md" className="athlete-profile-page__activity-card">
        <h2 className="athlete-profile-page__section-title">Activity</h2>
        <ActivityHeatmap
          entries={activityEntries}
          title="Training activity"
          subtitle={`${athlete.name.split(' ')[0]} over the last 52 weeks`}
        />
      </GlassCard>

      <div className="athlete-profile-page__grid">
        <GlassCard size="md" className="athlete-profile-page__radar-card">
          <h2 className="athlete-profile-page__section-title">Movement profile</h2>
          <RadarChart scores={scores} size={260} />
        </GlassCard>

        <GlassCard size="md" className="athlete-profile-page__list-card">
          <h2 className="athlete-profile-page__section-title">Recent workout logs</h2>
          <div className="athlete-profile-page__list">
            {logs.length === 0 && <p className="athlete-profile-page__empty-label">No logs yet.</p>}
            {logs.slice(0, 5).map((log) => {
              const percent = Math.round((log.workOutputAchieved / log.workOutputTarget) * 100);
              return (
                <div className="athlete-profile-page__list-item" key={log.id}>
                  <div>
                    <p className="athlete-profile-page__item-title">{log.date}</p>
                    <p className="athlete-profile-page__item-sub">{formatWorkOutput(log.workOutputAchieved)} achieved</p>
                  </div>
                  <span className="athlete-profile-page__item-pill">{percent}%</span>
                </div>
              );
            })}
          </div>
        </GlassCard>
      </div>
    </div>
  );
}
